import React, { useMemo } from 'react';

import { Dialog, DialogTitle, DialogContent, Box, Typography, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { useShortcuts } from '@/context/ShortcutContext';

import type { ShortcutDefinition } from '@/context/ShortcutContext';

import styles from './ShortcutsOverlay.module.scss';

interface ShortcutsOverlayProps {
  open: boolean;
  onClose: () => void;
}

function formatKeys(shortcut: ShortcutDefinition): string[] {
  const keys: string[] = [];
  if (shortcut.sequence) {
    keys.push(shortcut.sequence.toUpperCase());
    keys.push(shortcut.key.toUpperCase());
    return keys;
  }
  if (shortcut.ctrl) keys.push('Ctrl');
  if (shortcut.alt) keys.push('Alt');
  if (shortcut.shift) keys.push('Shift');
  keys.push(shortcut.key.length === 1 ? shortcut.key.toUpperCase() : shortcut.key);
  return keys;
}

export const ShortcutsOverlay: React.FC<ShortcutsOverlayProps> = React.memo(({ open, onClose }) => {
  const { getAll } = useShortcuts();

  const grouped = useMemo(() => {
    const all = getAll();
    const globalShortcuts = all.filter(s => s.scope === 'global');
    const timesheetShortcuts = all.filter(s => s.scope === 'timesheet');
    return { global: globalShortcuts, timesheet: timesheetShortcuts };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAll, open]);

  const renderSection = (title: string, shortcuts: ShortcutDefinition[]): React.ReactNode => {
    if (shortcuts.length === 0) return null;
    return (
      <Box className={styles.shortcutsOverlaySection}>
        <Typography className={styles.shortcutsOverlaySectionTitle}>{title}</Typography>
        {shortcuts.map((s, i) => (
          <Box key={`${s.label}-${i}`} className={styles.shortcutsOverlayRow}>
            <Box className={styles.shortcutsOverlayKeys}>
              {formatKeys(s).map((k, ki) => (
                <React.Fragment key={ki}>
                  {ki > 0 && (
                    <Typography className={styles.shortcutsOverlayPlus}>
                      {s.sequence ? 'then' : '+'}
                    </Typography>
                  )}
                  <Typography component="kbd" className={styles.shortcutsOverlayKbd}>{k}</Typography>
                </React.Fragment>
              ))}
            </Box>
            <Typography className={styles.shortcutsOverlayLabel}>{s.description}</Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle className={styles.shortcutsOverlayTitle}>
        Keyboard Shortcuts
        <IconButton onClick={onClose} className={styles.shortcutsOverlayCloseButton} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={styles.shortcutsOverlayContent}>
        {renderSection('Global', grouped.global)}
        {renderSection('Timesheet', grouped.timesheet)}
      </DialogContent>
    </Dialog>
  );
});

ShortcutsOverlay.displayName = 'ShortcutsOverlay';
